import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Send, Loader2, Paperclip, User } from 'lucide-react';
import { Button } from '../ui/button';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export function EmailComposer({ deal, onClose, onSent, prefillTemplate }) {
  const [to, setTo] = useState(deal?.client_email || '');
  const [cc, setCc] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [showCc, setShowCc] = useState(false);

  useEffect(() => {
    if (prefillTemplate) {
      setSubject(prefillTemplate.subject || '');
      setBody(prefillTemplate.body || '');
      if (prefillTemplate.to) setTo(prefillTemplate.to);
    }
  }, [prefillTemplate]);

  const handleSend = async () => {
    if (!to || !subject || !body) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/crm/email/send`, {
        deal_id: deal.id,
        to_email: to,
        subject,
        body,
        cc_email: cc || undefined
      });
      toast.success('Email sent successfully');
      onSent?.();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  const replacePlaceholders = (text) => {
    return text
      .replace(/{client_name}/g, deal?.client_name || '')
      .replace(/{business_name}/g, deal?.business_name || '')
      .replace(/{amount}/g, `$${(deal?.amount || 0).toLocaleString('en-AU')}`)
      .replace(/{finance_type}/g, deal?.finance_type || '')
      .replace(/{asset_description}/g, deal?.asset_description || '')
      .replace(/{term_months}/g, deal?.term_months || '60');
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-navy-800 rounded-xl border border-white/10 w-full max-w-2xl max-h-[85vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="email-composer">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
          <h3 className="text-white font-semibold">Compose Email</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X className="h-5 w-5" /></button>
        </div>

        {/* Fields */}
        <div className="px-5 py-4 space-y-3">
          <div className="flex items-center gap-3">
            <label className="text-gray-400 text-sm w-12">To</label>
            <input
              type="email"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              data-testid="email-to"
              className="flex-1 px-3 py-2 bg-navy-900 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-purple-500"
              placeholder="recipient@email.com"
            />
            {!showCc && (
              <button onClick={() => setShowCc(true)} className="text-gray-500 hover:text-gray-300 text-xs">Cc</button>
            )}
          </div>

          {showCc && (
            <div className="flex items-center gap-3">
              <label className="text-gray-400 text-sm w-12">Cc</label>
              <input
                type="email"
                value={cc}
                onChange={(e) => setCc(e.target.value)}
                data-testid="email-cc"
                className="flex-1 px-3 py-2 bg-navy-900 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-purple-500"
                placeholder="cc@email.com"
              />
            </div>
          )}

          <div className="flex items-center gap-3">
            <label className="text-gray-400 text-sm w-12">Subject</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              data-testid="email-subject"
              className="flex-1 px-3 py-2 bg-navy-900 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-purple-500"
              placeholder="Email subject"
            />
          </div>

          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            data-testid="email-body"
            rows={14}
            className="w-full px-4 py-3 bg-navy-900 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-purple-500 resize-none leading-relaxed"
            placeholder="Write your email..."
          />
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-3 border-t border-white/5">
          <div className="text-gray-500 text-xs">
            Sending from Flash Funding
          </div>
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={onClose} className="text-gray-400">Cancel</Button>
            <Button
              onClick={handleSend}
              disabled={sending || !to || !subject || !body}
              className="bg-purple-600 hover:bg-purple-700 text-white"
              data-testid="email-send-btn"
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
              Send Email
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function LenderSubmissionModal({ deal, onClose, onSubmitted }) {
  const [lenders, setLenders] = useState([]);
  const [selectedLender, setSelectedLender] = useState('');
  const [lenderEmail, setLenderEmail] = useState('');
  const [subject, setSubject] = useState(`Broker Submission - ${deal?.client_name} - ${deal?.business_name || ''} - $${(deal?.amount || 0).toLocaleString('en-AU')}`);
  const [body, setBody] = useState('');
  const [includeAI, setIncludeAI] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchLenders();
    generateBody();
  }, []);

  const fetchLenders = async () => {
    try {
      const res = await axios.get(`${API}/crm/lender-contacts`);
      setLenders(res.data);
    } catch (e) { console.error(e); }
  };

  const generateBody = () => {
    setBody(`Dear Lender,

Please find below a new finance application for your consideration.

Client: ${deal?.client_name}
Business: ${deal?.business_name || 'N/A'}
ABN: ${deal?.abn || 'N/A'}
Industry: ${deal?.industry || 'N/A'}
Years Trading: ${deal?.years_trading || 'N/A'}

Finance Type: ${deal?.finance_type || 'N/A'}
Amount Required: $${(deal?.amount || 0).toLocaleString('en-AU')}
Term: ${deal?.term_months || 60} months
Balloon/Residual: ${deal?.balloon_percent || 0}%

Asset: ${deal?.asset_description || 'N/A'}
Condition: ${deal?.is_new_or_used || 'N/A'}

I look forward to your indicative approval.

Kind regards,
Andrew
Flash Funding
1800 003 527`);
  };

  const handleLenderSelect = (name) => {
    setSelectedLender(name);
    const lender = lenders.find(l => l.name === name);
    if (lender?.email) setLenderEmail(lender.email);
    else setLenderEmail('');
  };

  const handleSubmit = async () => {
    if (!selectedLender || !lenderEmail) {
      toast.error('Please select a lender and enter their email');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/crm/lender/submit`, {
        deal_id: deal.id,
        lender_name: selectedLender,
        lender_email: lenderEmail,
        subject,
        body,
        include_ai_summary: includeAI
      });
      
      // Save lender email if new
      if (lenderEmail) {
        await axios.post(`${API}/crm/lender-contacts`, { name: selectedLender, email: lenderEmail }).catch(() => {});
      }
      
      toast.success(res.data.message);
      onSubmitted?.();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-navy-800 rounded-xl border border-white/10 w-full max-w-2xl max-h-[85vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="lender-submission-modal">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/5 bg-gradient-to-r from-purple-600/10 to-blue-600/10">
          <div>
            <h3 className="text-white font-semibold">Submit to Lender</h3>
            <p className="text-gray-400 text-xs">{deal?.client_name} — ${(deal?.amount || 0).toLocaleString('en-AU')}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X className="h-5 w-5" /></button>
        </div>

        <div className="px-5 py-4 space-y-4">
          {/* Lender Selection */}
          <div>
            <label className="text-gray-400 text-sm mb-1.5 block">Select Lender</label>
            <select
              value={selectedLender}
              onChange={(e) => handleLenderSelect(e.target.value)}
              data-testid="lender-select"
              className="w-full px-3 py-2.5 bg-navy-900 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-purple-500"
            >
              <option value="">Choose a lender...</option>
              {lenders.map(l => (
                <option key={l.name} value={l.name}>{l.name}{l.email ? ` (${l.email})` : ''}</option>
              ))}
            </select>
          </div>

          {/* Lender Email */}
          <div>
            <label className="text-gray-400 text-sm mb-1.5 block">Lender Email</label>
            <input
              type="email"
              value={lenderEmail}
              onChange={(e) => setLenderEmail(e.target.value)}
              data-testid="lender-email"
              className="w-full px-3 py-2.5 bg-navy-900 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-purple-500"
              placeholder="lender@email.com"
            />
          </div>

          {/* Subject */}
          <div>
            <label className="text-gray-400 text-sm mb-1.5 block">Subject</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              data-testid="lender-subject"
              className="w-full px-3 py-2.5 bg-navy-900 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-purple-500"
            />
          </div>

          {/* Body */}
          <div>
            <label className="text-gray-400 text-sm mb-1.5 block">Submission Email</label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              data-testid="lender-body"
              rows={12}
              className="w-full px-4 py-3 bg-navy-900 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-purple-500 resize-none leading-relaxed"
            />
          </div>

          {/* AI Summary Toggle */}
          <label className="flex items-center gap-3 cursor-pointer" data-testid="ai-summary-toggle">
            <input
              type="checkbox"
              checked={includeAI}
              onChange={(e) => setIncludeAI(e.target.checked)}
              className="w-4 h-4 rounded border-gray-600 bg-navy-900 text-purple-500 focus:ring-purple-500"
            />
            <span className="text-gray-300 text-sm">Include AI-generated broker submission summary</span>
          </label>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-3 border-t border-white/5">
          <div className="text-gray-500 text-xs">
            Deal will be moved to "Submitted to Lender" stage
          </div>
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={onClose} className="text-gray-400">Cancel</Button>
            <Button
              onClick={handleSubmit}
              disabled={submitting || !selectedLender || !lenderEmail}
              className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white"
              data-testid="submit-to-lender-btn"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
              Submit to Lender
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
